import { defineStore } from "pinia";
import _ from "lodash";
import { ProcessService } from "@/services/administry.service";
import { ProcesServiceDto } from "@/dto/appointment/appointment.dto";
import { useAuthStore } from "@/store/modules/auth";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

export const useHealthCenterList = defineStore("healthCenterList", {
  state: () => ({
    allBirthList: [] as ProcesServiceDto[],
    allDeathList: [] as ProcesServiceDto[],
    birthList: [] as ProcesServiceDto[],
    deathList: [] as ProcesServiceDto[],
    startDate: null as string | null,
    endDate: null as string | null,
  }),

  getters: {
    healthCenters(state) {
      const birthCounts = _.groupBy(state.birthList, "health_center");
      const deathCounts = _.groupBy(state.deathList, "health_center");
      const allCenters = new Set([...Object.keys(birthCounts), ...Object.keys(deathCounts)]);
      return Array.from(allCenters).map((center) => ({
        name: center || "N/A",
        births: birthCounts[center]?.length || 0,
        deaths: deathCounts[center]?.length || 0,
      }));
    },
  },

  actions: {
    filterByDateRange(list: ProcesServiceDto[], startDate: string | null, endDate: string | null) {
      if (!startDate || !endDate) return list;
      return _.filter(list, (item) => {
        const createdAt = dayjs(item.created_at);
        return createdAt.isBetween(startDate, endDate, null, "[]");
      });
    },

    async loadBirthList(payload: Record<string, any> = {}) {
      try {
        const service = ProcessService.getInstance(useAuthStore().getAccessToken);
        const response = await service.getBirthList(payload);
        if (response.data && Array.isArray(response.data)) {
          this.allBirthList = response.data;
          this.birthList = _.cloneDeep(response.data);
        }
      } catch (error) {
        console.error("Error loading birth list:", error);
      }
    },

    async loadDeathList(payload: Record<string, any> = {}) {
      try {
        const service = ProcessService.getInstance(useAuthStore().getAccessToken);
        const response = await service.getDeathList(payload);
        if (response.data && Array.isArray(response.data)) {
          this.allDeathList = response.data;
          this.deathList = _.cloneDeep(response.data);
        }
      } catch (error) {
        console.error("Error loading death list:", error);
      }
    },

    async loadAllData(payload: Record<string, any> = {}) {
      try {
        await Promise.all([this.loadBirthList(payload), this.loadDeathList(payload)]);
      } catch (error) {
        console.error("Error loading all data:", error);
      }
    },

    resetData() {
      this.birthList = _.cloneDeep(this.allBirthList);
      this.deathList = _.cloneDeep(this.allDeathList);
    },

    async applyDateFilter(startDate: string | null, endDate: string | null) {
      if (!startDate || !endDate) {
        this.resetData();
        return;
      }
      this.startDate = startDate;
      this.endDate = endDate;
      this.birthList = this.filterByDateRange(this.allBirthList, startDate, endDate);
      this.deathList = this.filterByDateRange(this.allDeathList, startDate, endDate);
    },

    async filterLastMonth() {
      const startDate = dayjs().subtract(1, "month").startOf("month").format();
      const endDate = dayjs().subtract(1, "month").endOf("month").format();
      await this.applyDateFilter(startDate, endDate);
    },

    async filterLastQuarter() {
      const startDate = dayjs().subtract(3, "month").startOf("month").format();
      const endDate = dayjs().subtract(1, "month").endOf("month").format();
      await this.applyDateFilter(startDate, endDate);
    },

    async filterLastSemester() {
      const startDate = dayjs().subtract(6, "month").startOf("month").format();
      const endDate = dayjs().subtract(1, "month").endOf("month").format();
      await this.applyDateFilter(startDate, endDate);
    },

    async filterCurrentYear() {
      const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
      const endDate = dayjs().endOf("year").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },
    
    async filterPreviousYear() {
      const startDate = dayjs().subtract(1, "year").startOf("year").format("YYYY-MM-DD");
      const endDate = dayjs().subtract(1, "year").endOf("year").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },
  },
});
